/**
 * Framework-free feature toggle evaluation. Nothing in here depends on React,
 * so it can be unit-tested directly and reused by the provider and in tests.
 */
import { AppEnv, getAppEnv } from '../shared/appEnv';
import { isBrowser } from '../shared/Utils';
import { featureTogglesDefault } from './envs/default';
import { featureTogglesDev } from './envs/dev';
import { featureTogglesLocal } from './envs/local';
import { featureTogglesStage } from './envs/stage';
import {
    CONFIG_TOGGLE_PREFIX,
    FeatureToggleName,
    IFeatureToggles,
} from './featureToggles.types';

/** Prefix of the sessionStorage keys used for non-prod overrides. */
export const SESSION_OVERRIDE_KEY_PREFIX = `TOGGLE_`;

/** sessionStorage key holding the non-prod override for a toggle. */
export const getSessionOverrideKey = (name: FeatureToggleName): string =>
    `${SESSION_OVERRIDE_KEY_PREFIX}${name}`;

/** True for `Config_` toggles, which only ever take their env config value. */
export const isConfigToggle = (name: FeatureToggleName): boolean =>
    name.startsWith(CONFIG_TOGGLE_PREFIX);

/**
 * The env config for an App_Env. Production, and any environment without its
 * own config, gets the production defaults.
 */
export const getLocalFeatureTogglesByEnv = (appEnv: AppEnv): IFeatureToggles => {
    switch (appEnv) {
        case AppEnv.Stage:
            return featureTogglesStage;
        case AppEnv.Dev:
            return featureTogglesDev;
        case AppEnv.Local:
            return featureTogglesLocal;
        case AppEnv.Prod:
        default:
            return featureTogglesDefault;
    }
};

/** Flatten an env config into a name → isEnabled map. */
export const getFeatureTogglesMap = (
    config: IFeatureToggles,
): Map<FeatureToggleName, boolean> =>
    new Map(config.toggles.map(({ name, isEnabled }) => [name, isEnabled]));

/**
 * Read the non-prod sessionStorage override for a toggle.
 *
 * Returns `undefined` when there is no override: on the server, for `Config_`
 * toggles, when the key is not set, or when storage is unavailable (for
 * example blocked by browser privacy settings).
 */
const readSessionOverride = (name: FeatureToggleName): boolean | undefined => {
    if (!isBrowser || isConfigToggle(name)) return undefined;

    try {
        const value = window.sessionStorage.getItem(getSessionOverrideKey(name));
        return value === null ? undefined : value === `true`;
    } catch {
        return undefined;
    }
};

/**
 * Whether a toggle is on.
 *
 * Precedence, highest first:
 * 1. Non-prod only, in the browser, non-`Config_` toggles: the sessionStorage
 *    override `TOGGLE_<name>` (`'true'` is on, any other value is off).
 * 2. The value in `availableTogglesMap`.
 * 3. `false` for a name that is not in the map.
 *
 * In production sessionStorage is never read.
 */
export const isFeatureToggleEnabled = (
    toggleNameToCheck: FeatureToggleName,
    availableTogglesMap: Map<FeatureToggleName, boolean>,
    isProd: boolean,
): boolean => {
    if (!isProd) {
        const override = readSessionOverride(toggleNameToCheck);
        if (override !== undefined) return override;
    }

    return availableTogglesMap?.get(toggleNameToCheck) ?? false;
};

/**
 * A toggle's configured value for an App_Env (defaults to the current one),
 * straight from the env config with no sessionStorage override. For test setup
 * and mock data.
 */
export const getToggleValueForEnv = (
    name: FeatureToggleName,
    appEnv: AppEnv = getAppEnv(),
): boolean =>
    getFeatureTogglesMap(getLocalFeatureTogglesByEnv(appEnv)).get(name) ??
    false;
